import { memo, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Input, Select, DatePicker, InputNumber, Spin, message } from "antd";
import { ArrowLeftOutlined, EditOutlined } from "@ant-design/icons";
import { OrganizationAPIs, Organization_Create_Request, Organization_GetOne_Response } from "~/common/typescript/apis/OrganizationAPIs";
import { Organization_Base } from "~/common/typescript/models/Organization";
import {
  getLegalTypeOptions,
  getIndustryOptions,
  getOrganizationStatusOptions,
  ORGANIZATION_DEFAULT_COUNTRY,
  ORGANIZATION_DEFAULT_STATUS
} from "~/common/typescript/enums/OrganizationEnums";
import FormItemWrapper from "~/components/common/FormItemWrapper/FormItemWrapper";
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;

const OrganizationUpdateScreen = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [isLoading, setLoading] = useState(false);
  const [isLoadingData, setLoadingData] = useState(true);
  const [organization, setOrganization] = useState<Organization_GetOne_Response | null>(null);

  // Basic info
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [maxNMembers, setMaxNMembers] = useState(5);

  // Tax and identification
  const [shortName, setShortName] = useState('');
  const [code, setCode] = useState('');
  const [taxId, setTaxId] = useState('');

  // Legal and business type
  const [legalType, setLegalType] = useState('');
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [registrationDate, setRegistrationDate] = useState('');

  // Contact and address
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [addressLine, setAddressLine] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState(ORGANIZATION_DEFAULT_COUNTRY);
  const [postalCode, setPostalCode] = useState('');

  // Activity and status
  const [industry, setIndustry] = useState('');
  const [foundedDate, setFoundedDate] = useState('');
  const [status, setStatus] = useState(ORGANIZATION_DEFAULT_STATUS);

  // Load organization data
  useEffect(() => {
    if (!id) {
      message.error('Organization ID is required');
      navigate('/organization');
      return;
    }

    const loadOrganization = async () => {
      try {
        setLoadingData(true);
        const data = await OrganizationAPIs.remote_getOne(parseInt(id));
        if (data) {
          setOrganization(data);

          // Populate form fields
          setName(data.name || '');
          setDescription(data.description || '');
          setMaxNMembers(data.maxNMembers || 5);

          // Tax and identification
          setShortName(data.shortName || '');
          setCode(data.code || '');
          setTaxId(data.taxId || '');

          // Legal and business type
          setLegalType(data.legalType || '');
          setRegistrationNumber(data.registrationNumber || '');
          setRegistrationDate(data.registrationDate || '');

          // Contact and address
          setEmail(data.email || '');
          setPhone(data.phone || '');
          setWebsite(data.website || '');
          setAddressLine(data.addressLine || '');
          setCity(data.city || '');
          setCountry(data.country || ORGANIZATION_DEFAULT_COUNTRY);
          setPostalCode(data.postalCode || '');

          // Activity and status
          setIndustry(data.industry || '');
          setFoundedDate(data.foundedDate || '');
          setStatus(data.status || ORGANIZATION_DEFAULT_STATUS);
        }
      } catch (error) {
        console.error('Failed to load organization:', error);
        message.error('Failed to load organization data');
        navigate('/organization');
      } finally {
        setLoadingData(false);
      }
    };

    loadOrganization();
  }, [id, navigate]);

  const onClickUpdateOrg = async () => {
    if (!id || !organization) {
      message.error('Organization data is not available');
      return;
    }

    if (!name.trim()) {
      message.error('Tên tổ chức là bắt buộc');
      return;
    }

    try {
      setLoading(true);
      const request: Organization_Create_Request = {
        name,
        description,
        maxNMembers,

        // Tax and identification
        shortName: shortName || undefined,
        code: code || undefined,
        taxId: taxId || undefined,

        // Legal and business type
        legalType: legalType || undefined,
        registrationNumber: registrationNumber || undefined,
        registrationDate: registrationDate || undefined,

        // Contact and address
        email: email || undefined,
        phone: phone || undefined,
        website: website || undefined,
        addressLine: addressLine || undefined,
        city: city || undefined,
        country: country || undefined,
        postalCode: postalCode || undefined,

        // Activity and status
        industry: industry || undefined,
        foundedDate: foundedDate || undefined,
        status: status || undefined,
      };

      await OrganizationAPIs.remote_update(parseInt(id), request);
      message.success('Cập nhật tổ chức thành công!');
      navigate(`/organization/${id}`);
    } catch (error) {
      console.error('Update failed:', error);
      message.error('Cập nhật tổ chức thất bại. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const onClickBack = () => {
    navigate(-1);
  };

  if (isLoadingData) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!organization) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Không tìm thấy tổ chức</h2>
        <Button onClick={onClickBack}>Quay lại</Button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', height: '100%' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onClickBack}
            style={{ marginRight: '16px' }}
          >
            Quay lại
          </Button>
          <h2 style={{ margin: 0 }}>Chỉnh sửa tổ chức: {organization.name}</h2>
        </div>
      </div>

      <div style={{
        maxHeight: 'calc(100vh - 120px)',
        overflowY: 'auto',
        padding: '16px',
        backgroundColor: '#fff',
        borderRadius: '8px',
        border: '1px solid #d9d9d9'
      }}>

        {/* Basic Information */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            marginBottom: '16px',
            color: '#1890ff',
            fontSize: '16px',
            fontWeight: '600',
            paddingBottom: '8px',
            borderBottom: '2px solid #1890ff'
          }}>
            Thông tin cơ bản
          </h3>

          <FormItemWrapper label="Tên tổ chức *">
            <Input
              placeholder="Tên đầy đủ của tổ chức"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="large"
            />
          </FormItemWrapper>

          <FormItemWrapper label="Mô tả">
            <TextArea
              placeholder="Mô tả về tổ chức"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              size="large"
            />
          </FormItemWrapper>
        </div>

        {/* Tax and Identification */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            marginBottom: '16px',
            color: '#52c41a',
            fontSize: '16px',
            fontWeight: '600',
            paddingBottom: '8px',
            borderBottom: '2px solid #52c41a'
          }}>
            Nhận dạng và thuế
          </h3>

          <FormItemWrapper label="Tên viết tắt">
            <Input
              placeholder="Tên giao dịch hoặc viết tắt (VD: ABC Corp)"
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              size="large"
            />
          </FormItemWrapper>

          <FormItemWrapper label="Mã tổ chức *">
            <Input
              placeholder="Mã nội bộ hoặc mã số doanh nghiệp (MSDN)"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              size="large"
            />
          </FormItemWrapper>

          <FormItemWrapper label="Mã số thuế">
            <Input
              placeholder="Mã số thuế (MST) - dùng cho hóa đơn điện tử"
              value={taxId}
              onChange={(e) => setTaxId(e.target.value)}
              size="large"
            />
          </FormItemWrapper>
        </div>

        {/* Legal Information */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            marginBottom: '16px',
            color: '#fa8c16',
            fontSize: '16px',
            fontWeight: '600',
            paddingBottom: '8px',
            borderBottom: '2px solid #fa8c16'
          }}>
            Thông tin pháp lý
          </h3>

          <FormItemWrapper label="Loại hình doanh nghiệp *">
            <Select
              placeholder="Chọn loại hình tổ chức"
              value={legalType}
              onChange={setLegalType}
              style={{ width: '100%' }}
              size="large"
            >
              {getLegalTypeOptions().map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          </FormItemWrapper>

          <FormItemWrapper label="Số đăng ký kinh doanh">
            <Input
              placeholder="Số giấy phép đăng ký kinh doanh"
              value={registrationNumber}
              onChange={(e) => setRegistrationNumber(e.target.value)}
              size="large"
            />
          </FormItemWrapper>

          <FormItemWrapper label="Ngày đăng ký">
            <DatePicker
              placeholder="Chọn ngày đăng ký kinh doanh"
              value={registrationDate ? dayjs(registrationDate) : null}
              onChange={(date) => setRegistrationDate(date ? date.format('YYYY-MM-DD') : '')}
              style={{ width: '100%' }}
              size="large"
            />
          </FormItemWrapper>
        </div>

        {/* Contact Information */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            marginBottom: '16px',
            color: '#722ed1',
            fontSize: '16px',
            fontWeight: '600',
            paddingBottom: '8px',
            borderBottom: '2px solid #722ed1'
          }}>
            Thông tin liên hệ
          </h3>

          <FormItemWrapper label="Email *">
            <Input
              placeholder="Email chính thức của tổ chức"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              size="large"
            />
          </FormItemWrapper>

          <FormItemWrapper label="Điện thoại *">
            <Input
              placeholder="Số điện thoại liên hệ"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              size="large"
            />
          </FormItemWrapper>

          <FormItemWrapper label="Website">
            <Input
              placeholder="https://example.com"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              size="large"
            />
          </FormItemWrapper>
        </div>

        {/* Address Information */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            marginBottom: '16px',
            color: '#f5222d',
            fontSize: '16px',
            fontWeight: '600',
            paddingBottom: '8px',
            borderBottom: '2px solid #f5222d'
          }}>
            Địa chỉ
          </h3>

          <FormItemWrapper label="Địa chỉ *">
            <Input
              placeholder="Số nhà, tên đường (VD: 123 Nguyễn Văn Linh)"
              value={addressLine}
              onChange={(e) => setAddressLine(e.target.value)}
              size="large"
            />
          </FormItemWrapper>

          <FormItemWrapper label="Thành phố *">
            <Input
              placeholder="Thành phố/Tỉnh (VD: Hồ Chí Minh)"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              size="large"
            />
          </FormItemWrapper>

          <FormItemWrapper label="Quốc gia *">
            <Input
              placeholder="Quốc gia"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              size="large"
            />
          </FormItemWrapper>

          <FormItemWrapper label="Mã bưu chính">
            <Input
              placeholder="Mã bưu chính (VD: 700000)"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value)}
              size="large"
            />
          </FormItemWrapper>
        </div>

        {/* Business Information */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{
            marginBottom: '16px',
            color: '#13c2c2',
            fontSize: '16px',
            fontWeight: '600',
            paddingBottom: '8px',
            borderBottom: '2px solid #13c2c2'
          }}>
            Thông tin kinh doanh
          </h3>

          <FormItemWrapper label="Ngành nghề">
            <Select
              placeholder="Chọn ngành nghề chính"
              value={industry}
              onChange={setIndustry}
              style={{ width: '100%' }}
              size="large"
            >
              {getIndustryOptions().map(ind => (
                <Option key={ind} value={ind}>{ind}</Option>
              ))}
            </Select>
          </FormItemWrapper>

          <FormItemWrapper label="Ngày thành lập">
            <DatePicker
              placeholder="Chọn ngày thành lập tổ chức"
              value={foundedDate ? dayjs(foundedDate) : null}
              onChange={(date) => setFoundedDate(date ? date.format('YYYY-MM-DD') : '')}
              style={{ width: '100%' }}
              size="large"
            />
          </FormItemWrapper>

          <FormItemWrapper label="Trạng thái *">
            <Select
              placeholder="Chọn trạng thái hoạt động"
              value={status}
              onChange={setStatus}
              style={{ width: '100%' }}
              size="large"
            >
              {getOrganizationStatusOptions().map(st => (
                <Option key={st} value={st}>{st}</Option>
              ))}
            </Select>
          </FormItemWrapper>
        </div>

        {/* Action Buttons */}
        <div style={{
          marginTop: '32px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          paddingTop: '24px',
          borderTop: '1px solid #f0f0f0'
        }}>
          <Button onClick={onClickBack} size="large">
            Hủy
          </Button>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={onClickUpdateOrg}
            loading={isLoading}
            size="large"
          >
            Cập nhật tổ chức
          </Button>
        </div>
      </div>
    </div>
  );
};

export default memo(OrganizationUpdateScreen);